import { useState } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { formatDistanceToNow } from 'date-fns';
import { useToast } from '@/hooks/use-toast';
import { Send, MessageCircle, Trash2 } from 'lucide-react';
import { usePostActions, useCommentReplies } from '@/hooks/usePosts';
import { useAuth } from '@/contexts/AuthContext';
import { useUserRole } from '@/hooks/useUserRole';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

interface CommentItemProps {
  comment: any;
  postId: string;
  onReplyAdded?: () => void;
  isReply?: boolean;
}

const CommentItem = ({ comment, postId, onReplyAdded, isReply = false }: CommentItemProps) => {
  const { t } = useTranslation();
  const [showReplies, setShowReplies] = useState(false);
  const [showReplyInput, setShowReplyInput] = useState(false);
  const [replyText, setReplyText] = useState('');
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
  
  const { addComment, isCommenting, deleteComment, isDeletingComment } = usePostActions();
  const { toast } = useToast();
  const { user } = useAuth();
  const { isAdminOrModerator } = useUserRole();
  
  const { data: replies, refetch: refetchReplies } = useCommentReplies(
    showReplies ? comment.id : undefined
  );

  const isCommentOwner = user && comment.user_id === user.id;
  const canDelete = isCommentOwner || isAdminOrModerator;

  const getUserInitials = (user: any) => {
    const name = user?.full_name || user?.display_name;
    if (name) {
      if (!name.includes(' ') && name.includes('.')) {
        const formatted = name.replace(/\./g, ' ');
        return formatted
          .split(' ')
          .map((n: string) => n.charAt(0))
          .join('')
          .toUpperCase()
          .slice(0, 2);
      }
      return name
        .split(' ')
        .map((n: string) => n.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return 'U';
  };

  const getUserDisplayName = (user: any) => {
    const name = user?.full_name || user?.display_name;
    if (name && !name.includes(' ') && name.includes('.')) {
      return name
        .split('.')
        .map((part: string) => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' ');
    }
    return name || 'User';
  };

  const handleReplySubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!replyText.trim()) return;
    
    try {
      await addComment({ 
        postId, 
        content: replyText,
        parentCommentId: comment.id 
      });
      
      setReplyText('');
      setShowReplyInput(false);
      toast({
        title: t('comments.replyAdded'),
        description: t('comments.replyAddedSuccess')
      });
      
      if (showReplies) {
        refetchReplies();
      }
      
      onReplyAdded?.();
    } catch (error: any) {
      toast({
        title: t('common.error'),
        description: error.message || t('comments.replyError'),
        variant: "destructive",
      });
    }
  };

  const handleDeleteComment = async () => {
    // Log admin deletion to audit log
    if (isAdminOrModerator && !isCommentOwner && user) {
      try {
        await supabase.from('admin_audit_log').insert({
          admin_user_id: user.id,
          action: 'delete_comment',
          target_user_id: comment.user_id || null,
          details: { comment_id: comment.id, post_id: postId, content_snippet: comment.content?.slice(0, 100) },
        });
      } catch (e) {
        console.error('Failed to log admin action:', e);
      }
    }

    deleteComment(comment.id, {
      onSuccess: () => {
        toast({
          title: t('comments.commentDeleted'),
          description: t('comments.commentDeletedSuccess'),
        });
        setIsDeleteAlertOpen(false);
        onReplyAdded?.(); // Trigger parent refresh
      },
      onError: (error) => {
        toast({
          title: t('common.error'),
          description: error?.message || t('common.unknownError'),
          variant: "destructive",
        });
      }
    });
  };

  return (
    <div className={cn("flex items-start", isReply && "ml-12")}>
      <Avatar className="h-8 w-8 mr-3" key={comment.user?.avatar_url || comment.user_id}>
        <AvatarImage 
          src={comment.user?.avatar_url} 
          alt={getUserDisplayName(comment.user)}
          loading="eager"
        />
        <AvatarFallback className="bg-pet-purple/20 text-pet-purple text-sm">
          {getUserInitials(comment.user)}
        </AvatarFallback>
      </Avatar>
      
      <div className="flex-1">
        <div className={cn(
          "rounded-lg p-3 relative group",
          isReply ? "bg-background" : "bg-muted"
        )}>
          <p className="text-sm">
            <span className="font-bold mr-2">{getUserDisplayName(comment.user)}</span>
            {comment.content}
          </p>
          
          {/* Delete button - visible on hover for owners and admins */}
          {canDelete && (
            <button
              onClick={() => setIsDeleteAlertOpen(true)}
              className="absolute top-2 right-2 p-1 rounded-full hover:bg-destructive/10 text-muted-foreground hover:text-destructive md:opacity-0 md:group-hover:opacity-100 transition-opacity"
              aria-label={t('common.delete')}
            >
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
        
        <div className="flex items-center gap-4 mt-1 px-3">
          <span className="text-xs text-muted-foreground">
            {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
          </span>
          
          {!isReply && (
            <button
              onClick={() => setShowReplyInput(!showReplyInput)}
              className="text-xs font-semibold text-muted-foreground hover:text-primary transition-colors"
            >
              {t('comments.reply')}
            </button>
          )}
          
          {!isReply && comment.replies_count > 0 && (
            <button
              onClick={() => setShowReplies(!showReplies)}
              className="text-xs font-semibold text-muted-foreground hover:text-primary transition-colors flex items-center gap-1"
            >
              <MessageCircle className="h-3 w-3" />
              {showReplies 
                ? (comment.replies_count === 1 ? t('comments.hideReply', { count: comment.replies_count }) : t('comments.hideReplies', { count: comment.replies_count }))
                : (comment.replies_count === 1 ? t('comments.viewReply', { count: comment.replies_count }) : t('comments.viewReplies', { count: comment.replies_count }))
              }
            </button>
          )}
        </div>
        
        {showReplyInput && (
          <form onSubmit={handleReplySubmit} className="mt-2 flex items-center gap-2">
            <Input
              placeholder={t('comments.replyTo', { name: getUserDisplayName(comment.user) })}
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              className="text-sm"
              disabled={isCommenting}
            />
            <Button
              type="submit"
              size="sm"
              disabled={!replyText.trim() || isCommenting}
            >
              <Send className="h-4 w-4" />
            </Button>
          </form>
        )}
        
        {showReplies && replies && replies.length > 0 && (
          <div className="mt-3 space-y-3">
            {replies.map((reply: any) => (
              <CommentItem
                key={reply.id}
                comment={reply}
                postId={postId}
                onReplyAdded={() => {
                  refetchReplies();
                  onReplyAdded?.();
                }}
                isReply={true}
              />
            ))}
          </div>
        )}
      </div>

      {/* Delete confirmation dialog */}
      <AlertDialog open={isDeleteAlertOpen} onOpenChange={setIsDeleteAlertOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('modals.deleteConfirm.deleteComment')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('modals.deleteConfirm.deleteCommentDescription')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('modals.deleteConfirm.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteComment}
              className="bg-red-500 text-white hover:bg-red-600"
              disabled={isDeletingComment}
            >
              {isDeletingComment ? t('modals.deleteConfirm.deleting') : t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CommentItem;
